import cx from 'classnames'
import { useContext, useMemo, useState } from 'react'

import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { getPriceNumber } from '@lib/format'
import { ShopContext } from '@lib/shop-context'
import { createFrameProduct } from '../lib/product'
import { parseShopifyGlobalId } from '../lib/shopify/client'
import { getStatePriceWithDiscount } from '../lib/state-price'
import { FrameBuilderCartContext } from '../contexts/cart-context'
import { FrameBuilderConfigurationContext } from '../contexts/configuration-context'
import { FrameBuilderLanguageContext } from '../contexts/langauge-context'
import { FrameBuilderCommittedStateContext } from '../contexts/state-context'
import { FrameBuilderStringsContext } from '../contexts/strings-context'
import { FrameBuilderValidationContext } from '../contexts/validation-context'

import Button from './utility/button'
import PricingControls from './pricing-controls'

interface FooterProps {
  getFrameImage: () => string | undefined
  onAddToCart?: () => void
  className?: string
}

const Footer = ({ getFrameImage, onAddToCart, className }: FooterProps) => {
  const { locale } = useContext(FrameBuilderLanguageContext)
  const { isCartProductAdding, addItemsToCart } = useContext(
    FrameBuilderCartContext,
  )
  const configuration = useContext(FrameBuilderConfigurationContext)
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { frameCompletionPercentage, stepPreviewAnimation } = useContext(
    FrameBuilderValidationContext,
  )
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { currencyCode } = useContext(ShopContext)

  const [quantity, setQuantity] = useState<string>('1')
  const [isQuantityValid, setIsQuantityValid] = useState(true)
  const [isProductBeingCreated, setIsProductBeingCreated] = useState(false)

  const isValidFrame = frameCompletionPercentage === 100 && isQuantityValid

  const price = useMemo<number>(
    () => getStatePriceWithDiscount(committedState, configuration),
    [committedState, configuration],
  )
  const isAddToCartInProgress = isCartProductAdding || isProductBeingCreated

  const handleAddToCartClick = async () => {
    const frameImage = getFrameImage()

    if (isAddToCartInProgress || !frameImage) {
      return
    }

    if (!isValidFrame) {
      stepPreviewAnimation.startBlinking()
      return
    }

    setIsProductBeingCreated(true)

    // Create frame product
    const frameProduct = await createFrameProduct(
      locale,
      committedState,
      frameImage,
    )

    const variantId = frameProduct?.variantId
      ? parseShopifyGlobalId(frameProduct.variantId)
      : undefined

    if (!variantId) {
      // Show error and cancel adding to cart
      alert(frameBuilderStrings.addProductError)
      setIsProductBeingCreated(false)
      return
    }

    // Add frame product to cart
    const isSuccessful = await addItemsToCart([
      {
        id: variantId,
        quantity: parseInt(quantity, 10),
      },
    ])

    if (isSuccessful) {
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart, {
        // TODO
        // currency: currencyCode,
        // value: getPriceNumber(activeVariant.price * parseInt(quantity, 10)),
        // items: [
        //   {
        //     index: 0,
        //     item_id: activeVariant.variantID,
        //     item_name: product.title,
        //     item_variant: activeVariant.options
        //       ?.map((option) => option.value)
        //       ?.join(', '),
        //     price: getPriceNumber(activeVariant.price),
        //     discount: activeVariant.comparePrice
        //       ? getPriceNumber(activeVariant.comparePrice - activeVariant.price)
        //       : 0,
        //     quantity: parseInt(quantity, 10),
        //   },
        // ],
      })
    }

    if (onAddToCart) {
      onAddToCart()
    }

    setIsProductBeingCreated(false)
  }

  return (
    <div
      className={cx(
        'flex flex-col justify-end bg-white border-t border-gray-300',
        className,
      )}
    >
      <PricingControls
        price={price}
        quantity={quantity}
        setQuantity={setQuantity}
        isQuantityValid={isQuantityValid}
        setIsQuantityValid={setIsQuantityValid}
        className="my-7"
      />
      <Button
        size="large"
        isVisuallyDisabled={!isValidFrame}
        disabled={isAddToCartInProgress}
        onClick={handleAddToCartClick}
      >
        {isAddToCartInProgress && frameBuilderStrings.controlAddingToCartLabel}
        {!isAddToCartInProgress && frameBuilderStrings.controlAddToCartLabel}
      </Button>
    </div>
  )
}

export default Footer
